import type { BadgeTone } from '@zarya/ui';
import { t } from '@app/i18n';
import type { CheckId, TriEstado } from './Runtime.api';

/**
 * Vocabulário de T15 — os checks do FR-090 na ordem em que a tela os mostra, com
 * os tooltips herdados do inventário do protótipo §2.11 (FR-132).
 *
 * O ajuste decidido em §2.11 substituiu os seis checks do protótipo pelos do
 * middleware + `NetworkReady` + expiração, tirou o **Gateway (PEP)** e removeu
 * "registration entry no SPIRE" do tooltip de identidade.
 *
 * **`network_ready` saiu desta lista em 30/08 (decisão de demonstração):** o
 * laboratório não terá NetworkPolicy antes de segunda, e o check estava falso em
 * todas as identidades. Ele **continua chegando no JSON** — o backend não parou de
 * o ler nem de o publicar —, só não é desenhado, e o `security_ready` deixou de o
 * considerar (`usecases/runtime/runtime.go` › `checksDoSecurityReady`). Para
 * voltar: reacrescentar a entrada aqui e o check naquela lista.
 */

/** Os checks na ordem em que a tela os mostra. Só os ids: o texto vem do catálogo. */
export const CHECK_IDS: CheckId[] = [
  'contract_approved',
  'admission_ready',
  'runtime_ready',
  'release_matches',
  'identity_ready',
  'authorization_ready',
  'identity_valid',
];

/**
 * Fase 2 (D5): «API no gateway» (a chave segue `edge_route`, decisão B do M2) entra na lista e NÃO conta no
 * SecurityReady — como o `network_ready` de 30/08, mas desenhado. Só para a identidade ligada a um recurso agente: o
 * servidor só manda a chave nesse caso, e é a presença dela que acende a linha.
 */
export const EDGE_ROUTE: CheckId = 'edge_route';

/**
 * «Identidade válida» (aim-ivi.1): o único check cujo `null` lido se chama «não verificável» (D2). Os outros `null`
 * são «indeterminado». Desde o aim-ivi.3 os dois contam igual, e deixam o SecurityReady indeterminado (AC-38).
 */
export const IDENTITY_VALID: CheckId = 'identity_valid';

/**
 * Os checks que contam e ficaram sem resposta do executor, na ordem da tela: o que o aviso do Security Ready
 * indeterminado nomeia (aim-ivi.3, decisão 5). Ficam fora os que a postura do site dispensa e a «API no gateway», que
 * não conta. O check ausente da resposta e o `null` contam igual.
 */
export function unansweredChecks(
  checks: Partial<Record<CheckId, TriEstado | undefined>>,
  notRequired: readonly string[] = [],
): CheckId[] {
  return CHECK_IDS.filter((id) => !notRequired.includes(id) && checks[id] == null);
}

export function checkIdsOf(checks: Partial<Record<CheckId, unknown>>): CheckId[] {
  return EDGE_ROUTE in checks ? [...CHECK_IDS, EDGE_ROUTE] : CHECK_IDS;
}

/** Rótulo e ⓘ de um check, do catálogo. */
export function check(id: CheckId): { rotulo: string; tip: string } {
  return { rotulo: t(`runtimeStatus.check.${id}`), tip: t(`runtimeStatus.check.${id}.tip`) };
}

export type TriKey = 'sim' | 'nao' | 'indeterminado';

const TRI_TOM: Record<TriKey, BadgeTone> = {
  sim: 'success',
  nao: 'danger',
  indeterminado: 'neutral',
};

/**
 * Badge de um check. Função, e não `Record` de módulo: o rótulo sai do catálogo
 * e uma tabela é avaliada na importação — com o seletor de idioma ela repintaria
 * no idioma anterior sem nada ficar vermelho. O tom continua tabela.
 */
export function tri(chave: TriKey): { rotulo: string; tone: BadgeTone } {
  return { rotulo: t(`runtimeStatus.tri.${chave}`), tone: TRI_TOM[chave] };
}

export function keyTri(valor: TriEstado | undefined): TriKey {
  if (valor === true) return 'sim';
  if (valor === false) return 'nao';
  return 'indeterminado';
}

const SECURITY_READY_TOM: Record<TriKey, BadgeTone> = {
  sim: 'success',
  nao: 'danger',
  indeterminado: 'neutral',
};

/** Badge SecurityReady do topo do card (FR-090). */
export function securityReady(chave: TriKey): { rotulo: string; tone: BadgeTone } {
  return { rotulo: t(`runtimeStatus.securityReady.${chave}`), tone: SECURITY_READY_TOM[chave] };
}

const AUTHORIZATION_TOM: Record<TriKey, BadgeTone> = {
  sim: 'success',
  nao: 'warning',
  indeterminado: 'neutral',
};

/** Estado do card **Desejado × publicado** (FR-092). */
export function authorization(chave: TriKey): { rotulo: string; tone: BadgeTone } {
  return { rotulo: t(`runtimeStatus.authorization.${chave}`), tone: AUTHORIZATION_TOM[chave] };
}

/** Rótulo de cada tipo de diferença do PDP; valor fora da lista sai como veio. */
export function difference(kind: string | null | undefined): string {
  if (!kind) return '—';
  const rotulo = t(`runtimeStatus.difference.${kind}`);
  // A chave crua de volta significa tipo que o catálogo não conhece: mostra o
  // valor do executor, que é o que existe, em vez da chave.
  return rotulo === `runtimeStatus.difference.${kind}` ? kind : rotulo;
}
